import { useCallback, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Caption1,
  Divider,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Spinner,
  Switch,
  Tab,
  TabList,
  Text,
  Tooltip,
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  ArrowDownload20Regular,
  ArrowEnter20Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  Code20Regular,
  Copy20Regular,
  Database20Regular,
  DocumentText20Regular,
  ErrorCircle20Filled,
  Flowchart20Regular,
  Globe20Regular,
  Mail20Regular,
  Search20Regular,
  Sparkle20Regular,
  Wrench20Regular,
} from '@fluentui/react-icons';
import {
  collectExceptions,
  collectLogs,
  countLogs,
  type LogEntry,
  countSpans,
  downloadLogExport,
  getConversationTelemetry,
  LOG_EXPORT_MAX_HOURS,
  severityLabel,
  TelemetryNotFoundError,
  type ConversationDiagnosticsResponse,
  type TelemetryNode,
  type TelemetryOperation,
} from '../../api/diagnosticsClient';
import { useAuthArgs } from '../../hooks/useAuthArgs';
import { downloadBlob, downloadTelemetryJson, downloadTelemetryLog } from '../../api/diagnosticsExport';
import { SplitPane } from '../components/SplitPane';

const useStyles = makeStyles({
  // Docked page: a header band at natural height, then one fill row for the split. The split sizes
  // itself against that row, so nothing here computes a height.
  page: {
    display: 'grid',
    gridTemplateRows: 'auto minmax(0, 1fr)',
    gap: tokens.spacingVerticalL,
    height: '100%',
    minHeight: 0,
  },
  header: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  // Same dock pattern one level down: badges/filter at natural height, split fills the rest.
  resultArea: {
    display: 'grid',
    gridTemplateRows: 'auto minmax(0, 1fr)',
    gap: tokens.spacingVerticalM,
    minHeight: 0,
  },
  resultHeader: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  // Tab host: tab strip at natural height, panel fills the rest so the panel scrolls, not the host.
  tabHost: {
    display: 'grid',
    gridTemplateRows: 'auto minmax(0, 1fr)',
    height: '100%',
    minHeight: 0,
  },
  tabList: { ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2) },
  tabCount: {
    marginLeft: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground3,
  },
  tabPanel: {
    overflowY: 'auto',
    minHeight: 0,
    paddingTop: tokens.spacingVerticalS,
  },
  logsHost: {
    display: 'grid',
    gridTemplateRows: 'auto minmax(0, 1fr)',
    minHeight: 0,
  },
  logsToolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
  },
  searchRow: { display: 'flex', gap: tokens.spacingHorizontalM, alignItems: 'end', flexWrap: 'wrap' },
  idInput: { minWidth: '380px' },
  detailPane: {
    minHeight: 0,
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    ...shorthands.padding(tokens.spacingVerticalM, tokens.spacingHorizontalM),
    overflowY: 'auto',
  },
  operation: {
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke2),
    ...shorthands.borderRadius(tokens.borderRadiusMedium),
    marginBottom: tokens.spacingVerticalM,
  },
  operationHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalM),
    cursor: 'pointer',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  grow: { flexGrow: 1, minWidth: 0 },

  // Column layout mirrors the App Insights end-to-end grid: what ran, where it went, how it ended,
  // how long it took, and when — so the eye can scan one column instead of parsing a sentence.
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    ...shorthands.padding('2px', tokens.spacingHorizontalM),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    color: tokens.colorNeutralForeground3,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    ...shorthands.padding('2px', tokens.spacingHorizontalM),
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  rowSelected: { backgroundColor: tokens.colorNeutralBackground1Selected },
  exceptionRow: { backgroundColor: tokens.colorPaletteRedBackground1 },
  exceptionTag: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
    letterSpacing: '0.4px',
    flexShrink: 0,
  },
  rowMatch: { backgroundColor: tokens.colorPaletteYellowBackground2 },
  nameCell: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS, width: '34%', minWidth: 0 },
  targetCell: { width: '22%', minWidth: 0, color: tokens.colorNeutralForeground3 },
  resultCell: { width: '52px', flexShrink: 0 },
  ellipsis: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' },
  durationCell: { width: '84px', flexShrink: 0, textAlign: 'right' },
  track: { flexGrow: 1, height: '14px', position: 'relative', backgroundColor: tokens.colorNeutralBackground3 },
  bar: { position: 'absolute', top: 0, bottom: 0, minWidth: '2px', ...shorthands.borderRadius('2px') },
  chevron: { flexShrink: 0, width: '20px', display: 'flex', justifyContent: 'center' },
  icon: { flexShrink: 0, display: 'flex', alignItems: 'center' },

  // Timeline ruler: without tick labels a bar chart shows proportion but never answers "how late?".
  ruler: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, ...shorthands.padding('0', tokens.spacingHorizontalM) },
  rulerTrack: { flexGrow: 1, position: 'relative', height: '16px' },
  rootDivider: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    ...shorthands.padding('2px', tokens.spacingHorizontalM),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    backgroundColor: tokens.colorNeutralBackground2,
  },
  rulerTick: { position: 'absolute', top: 0, bottom: 0, ...shorthands.borderLeft('1px', 'dashed', tokens.colorNeutralStroke2) },
  rulerLabel: { position: 'absolute', top: 0, left: '2px', fontSize: tokens.fontSizeBase100, color: tokens.colorNeutralForeground3, whiteSpace: 'nowrap' },

  logsBody: { overflowY: 'auto', minHeight: 0 },
  logRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    ...shorthands.padding('2px', tokens.spacingHorizontalM),
    cursor: 'pointer',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  // Fixed width: the badge text varies ("Information" vs "Warning"), and letting it size itself
  // pushed the following columns out of alignment row by row.
  logSeverityCell: { width: '92px', flexShrink: 0, display: 'flex' },
  logSpanCell: { width: '200px', flexShrink: 0, color: tokens.colorNeutralForeground3 },
  logTimeCell: { width: '96px', flexShrink: 0, color: tokens.colorNeutralForeground3 },

  detailRow: { display: 'flex', flexDirection: 'column', marginBottom: tokens.spacingVerticalS },
  propRow: { display: 'flex', gap: tokens.spacingHorizontalS, alignItems: 'baseline', marginBottom: '2px' },
  propKey: { width: '150px', flexShrink: 0, color: tokens.colorNeutralForeground3, wordBreak: 'break-word' },
  pre: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: tokens.fontSizeBase200,
    backgroundColor: tokens.colorNeutralBackground3,
    ...shorthands.padding(tokens.spacingVerticalS, tokens.spacingHorizontalS),
    ...shorthands.borderRadius(tokens.borderRadiusSmall),
    marginTop: tokens.spacingVerticalXS,
  },
  stack: { maxHeight: '260px', overflowY: 'auto' },
  empty: { color: tokens.colorNeutralForeground3 },
});

/** Bar colour by category, so the shape of a turn is readable without reading every label. */
function categoryColor(node: TelemetryNode): string {
  if (node.success === false) return tokens.colorPaletteRedBackground3;
  switch (node.category) {
    case 'Request': return tokens.colorBrandBackground;
    case 'Model': return tokens.colorPaletteBlueBackground2;
    case 'Tool': return tokens.colorPalettePurpleBackground2;
    case 'Redis': return tokens.colorPaletteBeigeBackground2;
    case 'SQL': return tokens.colorPaletteTealBackground2;
    case 'Messaging': return tokens.colorPaletteMarigoldBackground2;
    default: return tokens.colorNeutralForeground3;
  }
}

/**
 * An icon per category instead of a text badge.
 *
 * The badge version spent roughly a third of the name column restating a word, which is the column
 * that carries the actual information. App Insights uses glyphs for the same reason.
 */
function CategoryIcon({ node }: { node: TelemetryNode }) {
  const color = node.success === false ? tokens.colorPaletteRedForeground1 : undefined;
  const style = { color };
  switch (node.category) {
    case 'Request': return <ArrowEnter20Regular style={style} />;
    case 'Model': return <Sparkle20Regular style={style} />;
    case 'Tool': return <Wrench20Regular style={style} />;
    case 'Redis':
    case 'SQL': return <Database20Regular style={style} />;
    case 'Messaging': return <Mail20Regular style={style} />;
    case 'HTTP': return <Globe20Regular style={style} />;
    case 'Exception': return <ErrorCircle20Filled style={{ color: tokens.colorPaletteRedForeground1 }} />;
    default: return <Globe20Regular style={style} />;
  }
}

function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '';
  if (ms < 1) return `${Math.round(ms * 1000)} µs`;
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(2)} s`;
  // A conversation's wall clock runs to hours; "5747.24 s" is not a readable number.
  const totalSec = Math.round(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`;
}

/** Relative offset label ("+1.2 s", "+95m 3s") from a chosen origin. */
function formatOffset(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  return formatDuration(ms);
}

/**
 * How far a root's subtree extends past its own start, in ms.
 *
 * A root's own `durationMs` is not enough: a child that outlives its parent (a fire-and-forget
 * dependency, a projection kicked off mid-turn) would be clipped off the right edge.
 */
function subtreeExtentMs(root: TelemetryNode): number {
  const start = root.timestamp ? Date.parse(root.timestamp) : null;
  if (start === null) return root.durationMs ?? 0;

  let max = root.durationMs ?? 0;
  const walk = (n: TelemetryNode) => {
    if (n.timestamp) {
      const end = Date.parse(n.timestamp) - start + (n.durationMs ?? 0);
      if (end > max) max = end;
    }
    n.children.forEach(walk);
  };
  walk(root);
  return max;
}

/** Which primary-pane tab is showing. */
type TabValue = 'tree' | 'logs';

function matches(node: TelemetryNode, q: string): boolean {
  if (!q) return false;
  const hay = [node.name, node.target, node.detail, node.category, node.resultCode, node.method]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return hay.includes(q);
}

/**
 * Nodes to keep visible for a query: every match plus its ancestors, so a deep hit stays reachable
 * instead of being hidden behind a collapsed parent.
 */
function visibleFor(roots: TelemetryNode[], q: string): { keep: Set<TelemetryNode>; hits: TelemetryNode[] } {
  const keep = new Set<TelemetryNode>();
  const hits: TelemetryNode[] = [];

  const walk = (node: TelemetryNode, ancestors: TelemetryNode[]): boolean => {
    const self = matches(node, q);
    if (self) {
      hits.push(node);
    }

    let childHit = false;
    for (const c of node.children) {
      childHit = walk(c, [...ancestors, node]) || childHit;
    }

    // Exceptions render as their own rows, so a matching exception is a hit in its own right —
    // counting only spans reported "0 matches" while a matching exception row was on screen.
    const exHits = node.exceptions.filter((e) => matches(e, q));
    hits.push(...exHits);

    if (self || childHit || exHits.length > 0) {
      keep.add(node);
      for (const a of ancestors) keep.add(a);
      return true;
    }
    return false;
  };

  for (const r of roots) walk(r, []);
  return { keep, hits };
}

/** Formats a Date as the local `YYYY-MM-DDTHH:mm` value a datetime-local input expects. */
function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Admin → Diagnostics → Conversation Diagnostics.
 *
 * Paste a conversation id and get its end-to-end transaction view: every turn as a span tree with
 * durations, dependencies and exceptions. The same picture the Application Insights end-to-end blade
 * gives, without needing portal access or writing KQL — so support/engineering can answer "where did
 * the time go?" and "what threw?" from inside the product.
 */
export function ConversationDiagnosticsPage() {
  const styles = useStyles();
  const getAuthArgs = useAuthArgs();

  const [idInput, setIdInput] = useState('');
  const [result, setResult] = useState<ConversationDiagnosticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [selected, setSelected] = useState<TelemetryNode | null>(null);

  const [exportStart, setExportStart] = useState(() => toLocalInput(new Date(Date.now() - 60 * 60 * 1000)));
  const [exportEnd, setExportEnd] = useState(() => toLocalInput(new Date()));
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const exportLogs = useCallback(async () => {
    const start = new Date(exportStart);
    const end = new Date(exportEnd);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
      setExportError('Choose a start and an end, with the end after the start.');
      return;
    }
    if (end.getTime() - start.getTime() > LOG_EXPORT_MAX_HOURS * 3_600_000) {
      setExportError(`The range may not exceed ${LOG_EXPORT_MAX_HOURS} hours.`);
      return;
    }

    setExporting(true);
    setExportError(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setExportError('You must be signed in to export logs.');
        return;
      }
      const { filename, blob } = await downloadLogExport(start, end, auth);
      downloadBlob(filename, blob);
    } catch (e) {
      setExportError(e instanceof Error ? e.message : String(e));
    } finally {
      setExporting(false);
    }
  }, [exportStart, exportEnd, getAuthArgs]);

  const search = useCallback(async () => {
    const id = idInput.trim();
    if (!id) return;

    setLoading(true);
    setError(null);
    setNotice(null);
    setResult(null);
    setSelected(null);
    try {
      const auth = await getAuthArgs();
      if (!auth) {
        setError('You must be signed in to view conversation diagnostics.');
        return;
      }
      setResult(await getConversationTelemetry(id, auth));
    } catch (e) {
      if (e instanceof TelemetryNotFoundError) {
        setNotice(e.message);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setLoading(false);
    }
  }, [idInput, getAuthArgs]);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div>
          <Text as="h2" size={600} weight="semibold">Conversation Diagnostics</Text>
          <br />
          <Caption1 className={styles.empty}>
            End-to-end transaction view for a conversation — every turn, its dependencies, and where the time went.
          </Caption1>
        </div>

        <div className={styles.searchRow}>
          <Field label="Conversation ID" className={styles.idInput}>
            <Input
              value={idInput}
              placeholder="e.g. 84b35e18-886f-4ee3-88aa-183de2e0c678"
              onChange={(_, d) => setIdInput(d.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void search(); }}
            />
          </Field>
          <Button appearance="primary" icon={<Search20Regular />} onClick={() => void search()} disabled={loading || !idInput.trim()}>
            Search
          </Button>
        </div>

        <div className={styles.searchRow}>
          <Field label="Export logs from">
            <Input type="datetime-local" value={exportStart} onChange={(_, d) => setExportStart(d.value)} />
          </Field>
          <Field label="to">
            <Input type="datetime-local" value={exportEnd} onChange={(_, d) => setExportEnd(d.value)} />
          </Field>
          <Button icon={<ArrowDownload20Regular />} onClick={() => void exportLogs()} disabled={exporting}>
            Download logs (.zip)
          </Button>
        </div>
        {exporting && <Spinner label="Exporting logs — this can take a few minutes…" />}
        {exportError && <MessageBar intent="error"><MessageBarBody>{exportError}</MessageBarBody></MessageBar>}

        {loading && <Spinner label="Querying telemetry…" />}
        {error && <MessageBar intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
        {notice && <MessageBar intent="info"><MessageBarBody>{notice}</MessageBarBody></MessageBar>}
      </div>

      {/* Second grid row. Always present so the fill track exists even before a search. */}
      {result
        ? <ResultView result={result} selected={selected} onSelect={setSelected} styles={styles} />
        : <div />}
    </div>
  );
}

function ResultView({
  result,
  selected,
  onSelect,
  styles,
}: {
  result: ConversationDiagnosticsResponse;
  selected: TelemetryNode | null;
  onSelect: (n: TelemetryNode) => void;
  styles: ReturnType<typeof useStyles>;
}) {
  const { telemetry, source } = result;
  const [filter, setFilter] = useState('');
  const q = filter.trim().toLowerCase();

  const allRoots = useMemo(
    () => telemetry.operations.flatMap((o) => o.roots ?? []),
    [telemetry.operations],
  );
  const { keep, hits } = useMemo(() => visibleFor(allRoots, q), [allRoots, q]);

  const logs = useMemo(
    () =>
      allRoots
        .flatMap((r) => collectLogs(r))
        // Tree order groups by span, which made the timestamps jump around (15:52 then 17:26 then
        // back). A log list is read as a timeline, so order it as one.
        .sort((a, b) => {
          const ta = a.log.timestamp ? Date.parse(a.log.timestamp) : 0;
          const tb = b.log.timestamp ? Date.parse(b.log.timestamp) : 0;
          return ta - tb;
        }),
    [allRoots],
  );
  const spanTotal = useMemo(() => allRoots.reduce((n, r) => n + countSpans(r), 0), [allRoots]);

  const [tab, setTab] = useState<TabValue>('tree');

  return (
    <div className={styles.resultArea}>
      <div className={styles.resultHeader}>
        <div className={styles.searchRow}>
        <Badge appearance="tint" color={source === 'live' ? 'success' : 'informative'}>
          {source === 'live' ? 'Live query' : 'Archived snapshot'}
        </Badge>
        <Badge appearance="tint">{telemetry.operations.length} operation(s)</Badge>
        <Badge appearance="tint">{spanTotal} span(s)</Badge>
        {/* Exceptions have their own badge, so this one counts traces only — the tab label carries
            the combined total, which is what the list actually shows. */}
        <Badge appearance="tint">{logs.filter((e) => !e.isException).length} log(s)</Badge>
        {!telemetry.complete && (
          <Tooltip
            content="Some operations were still landing when this was captured. App Insights ingestion lags by several minutes."
            relationship="description"
          >
            <Badge appearance="tint" color="warning">Partial</Badge>
          </Tooltip>
        )}
        {telemetry.exceptions.length > 0 && (
          <Badge appearance="tint" color="danger">{telemetry.exceptions.length} exception(s)</Badge>
        )}
        <div className={styles.grow} />
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <MenuButton appearance="secondary" size="small" icon={<ArrowDownload20Regular />}>
              Download
            </MenuButton>
          </MenuTrigger>
          <MenuPopover>
            <MenuList>
              <MenuItem
                icon={<DocumentText20Regular />}
                onClick={() => downloadTelemetryLog(telemetry.conversationId, result, logs)}
              >
                Log file (.log)
              </MenuItem>
              <MenuItem
                icon={<Code20Regular />}
                onClick={() => downloadTelemetryJson(telemetry.conversationId, result)}
              >
                Raw telemetry (.json)
              </MenuItem>
            </MenuList>
          </MenuPopover>
        </Menu>
        </div>

        <div className={styles.searchRow}>
          <Field label="Filter spans" className={styles.idInput}>
            <Input
              value={filter}
              placeholder="name, target, result code, message…"
              contentBefore={<Search20Regular />}
              onChange={(_, d) => setFilter(d.value)}
            />
          </Field>
          {q && (
            <Caption1 className={styles.empty}>
              {hits.length} match{hits.length === 1 ? '' : 'es'}
            </Caption1>
          )}
        </div>
      </div>

      <SplitPane
        secondaryLabel="details panel"
        primary={
          // Tabs, not stacked sections: the span tree and the log list each want the full pane, and
          // expanding the logs inline squeezed the tree into a sliver.
          <div className={styles.tabHost}>
            <TabList
              selectedValue={tab}
              onTabSelect={(_, d) => setTab(d.value as TabValue)}
              className={styles.tabList}
            >
              <Tab value="tree" icon={<Flowchart20Regular />}>
                Transaction
                <Caption1 className={styles.tabCount}>{spanTotal}</Caption1>
              </Tab>
              <Tab value="logs" icon={<DocumentText20Regular />}>
                Logs &amp; traces
                <Caption1 className={styles.tabCount}>{logs.length}</Caption1>
              </Tab>
            </TabList>

            {tab === 'tree' ? (
              <div className={styles.tabPanel}>
                {telemetry.operations.length === 0 ? (
                  <Text className={styles.empty}>No operations found for this conversation.</Text>
                ) : (
                  telemetry.operations.map((op) => (
                    <OperationView
                      key={op.operationId}
                      operation={op}
                      selected={selected}
                      onSelect={onSelect}
                      query={q}
                      keep={keep}
                      styles={styles}
                    />
                  ))
                )}
              </div>
            ) : (
              <LogsPanel logs={logs} query={q} selected={selected} onSelect={onSelect} styles={styles} />
            )}
          </div>
        }
        secondary={
          <div className={styles.detailPane}>
            <DetailPane node={selected} styles={styles} />
          </div>
        }
      />
    </div>
  );
}

function OperationView({
  operation,
  selected,
  onSelect,
  query,
  keep,
  styles,
}: {
  operation: TelemetryOperation;
  selected: TelemetryNode | null;
  onSelect: (n: TelemetryNode) => void;
  query: string;
  keep: Set<TelemetryNode>;
  styles: ReturnType<typeof useStyles>;
}) {
  const [open, setOpen] = useState(true);
  const roots = operation.roots ?? [];
  const errors = useMemo(() => roots.flatMap((r) => collectExceptions(r)), [roots]);
  const spans = useMemo(() => roots.reduce((n, r) => n + countSpans(r), 0), [roots]);

  // Conversation start, used only to label how far into the conversation each root began.
  const convOriginMs = operation.startTime ? Date.parse(operation.startTime) : null;

  // Wall-clock span of the whole operation — the elapsed time of the conversation, not work done.
  const wallClockMs = useMemo(() => {
    if (convOriginMs === null) return operation.durationMs ?? null;
    const ends = roots
      .map((r) => (r.timestamp ? Date.parse(r.timestamp) - convOriginMs + subtreeExtentMs(r) : null))
      .filter((v): v is number => v !== null);
    return ends.length > 0 ? Math.max(...ends, operation.durationMs ?? 0) : operation.durationMs ?? null;
  }, [roots, convOriginMs, operation.durationMs]);

  const shown = query ? roots.filter((r) => keep.has(r)) : roots;
  if (query && shown.length === 0) return null;

  return (
    <div className={styles.operation}>
      <div className={styles.operationHeader} onClick={() => setOpen((o) => !o)}>
        <div className={styles.chevron}>{open ? <ChevronDown20Regular /> : <ChevronRight20Regular />}</div>
        <Text weight="semibold" className={styles.grow}>{operation.name ?? operation.operationId}</Text>
        <Tooltip content={`Trace  · conversation `} relationship="description">
          <Caption1 className={styles.empty}>trace {operation.operationId.slice(0, 8)}</Caption1>
        </Tooltip>
        {errors.length > 0 && <Badge appearance="tint" color="danger">{errors.length}</Badge>}
        {roots.length > 1 && <Caption1 className={styles.empty}>{roots.length} root(s)</Caption1>}
        <Caption1 className={styles.empty}>{spans} span(s)</Caption1>
        <Tooltip
          content="Elapsed wall-clock time across the whole conversation, including think time between turns."
          relationship="description"
        >
          <Text weight="semibold">{formatDuration(wallClockMs ?? operation.durationMs)}</Text>
        </Tooltip>
      </div>

      {open && (
        <>
          <div className={styles.headerRow}>
            <div className={styles.nameCell}><Caption1>EVENT</Caption1></div>
            <div className={styles.targetCell}><Caption1>TARGET</Caption1></div>
            <div className={styles.resultCell}><Caption1>RES.</Caption1></div>
            <Caption1 className={styles.durationCell}>DURATION</Caption1>
            <div className={styles.rulerTrack}><Caption1>TIMELINE (per root)</Caption1></div>
          </div>
          {shown.map((root, i) => (
            <RootGroup
              key={root.spanId ?? `root-${i}`}
              root={root}
              convOriginMs={convOriginMs}
              selected={selected}
              onSelect={onSelect}
              query={query}
              keep={keep}
              styles={styles}
            />
          ))}
        </>
      )}
    </div>
  );
}

/**
 * One root and its subtree, scaled to ITS OWN extent.
 *
 * Scaling every root against the operation is what App Insights does, but it only works when an
 * operation is one request. Here an operation is a whole conversation: the roots of this PPE example
 * span 1.6 hours of wall clock while individual turns run 119 ms to 30 s, so a shared scale crushed
 * every bar to a 1-pixel sliver and the timeline column rendered empty.
 * Scaling per root answers the question the page exists for ("where did the time go in this turn?"),
 * and the offset caption preserves where the turn sat in the conversation.
 */
function RootGroup({
  root,
  convOriginMs,
  selected,
  onSelect,
  query,
  keep,
  styles,
}: {
  root: TelemetryNode;
  convOriginMs: number | null;
  selected: TelemetryNode | null;
  onSelect: (n: TelemetryNode) => void;
  query: string;
  keep: Set<TelemetryNode>;
  styles: ReturnType<typeof useStyles>;
}) {
  const originMs = root.timestamp ? Date.parse(root.timestamp) : null;
  const extentMs = subtreeExtentMs(root);
  const offsetMs = originMs !== null && convOriginMs !== null ? originMs - convOriginMs : null;

  return (
    <>
      <div className={styles.rootDivider}>
        <Caption1 className={styles.empty}>
          {offsetMs !== null && offsetMs > 0 ? `+${formatOffset(offsetMs)} into conversation` : 'conversation start'}
        </Caption1>
        <Ruler totalMs={extentMs} styles={styles} />
      </div>
      <SpanRows
        node={root}
        depth={0}
        originMs={originMs}
        totalMs={extentMs}
        selected={selected}
        onSelect={onSelect}
        query={query}
        keep={keep}
        styles={styles}
      />
    </>
  );
}

/** Tick marks across the timeline column, so bar position reads as a time, not just a proportion. */
function Ruler({ totalMs, styles }: { totalMs: number | null; styles: ReturnType<typeof useStyles> }) {
  if (!totalMs || totalMs <= 0) return <div className={styles.track} />;
  const ticks = [0, 25, 50, 75];
  return (
    <div className={styles.rulerTrack}>
      {ticks.map((pct) => (
        <div key={pct} className={styles.rulerTick} style={{ left: `${pct}%` }}>
          <span className={styles.rulerLabel}>{formatOffset((totalMs * pct) / 100)}</span>
        </div>
      ))}
    </div>
  );
}

function SpanRows({
  node,
  depth,
  originMs,
  totalMs,
  selected,
  onSelect,
  query,
  keep,
  styles,
}: {
  node: TelemetryNode;
  depth: number;
  originMs: number | null;
  totalMs: number | null;
  selected: TelemetryNode | null;
  onSelect: (n: TelemetryNode) => void;
  query: string;
  keep: Set<TelemetryNode>;
  styles: ReturnType<typeof useStyles>;
}) {
  const [open, setOpen] = useState(true);
  const logCount = countLogs(node);
  const hasChildren = node.children.length > 0 || node.exceptions.length > 0;

  // Offset/width as a percentage of the operation, so a step that starts late is visibly late rather
  // than merely long. Falls back to a full-width bar when timing is unavailable.
  let leftPct = 0;
  let widthPct = 100;
  if (originMs !== null && totalMs && totalMs > 0 && node.timestamp) {
    const start = Date.parse(node.timestamp) - originMs;
    leftPct = Math.max(0, Math.min(100, (start / totalMs) * 100));
    widthPct = Math.max(0.5, Math.min(100 - leftPct, ((node.durationMs ?? 0) / totalMs) * 100));
  }

  const isSelected = selected === node;
  const isHit = query !== '' && matches(node, query);
  const children = query ? node.children.filter((c) => keep.has(c)) : node.children;

  return (
    <>
      <div
        className={mergeClasses(styles.row, isSelected && styles.rowSelected, isHit && styles.rowMatch)}
        onClick={() => onSelect(node)}
      >
        <div className={styles.nameCell} style={{ paddingLeft: `${depth * 16}px` }}>
          <div
            className={styles.chevron}
            onClick={(e) => { e.stopPropagation(); if (hasChildren) setOpen((o) => !o); }}
          >
            {hasChildren ? (open ? <ChevronDown20Regular /> : <ChevronRight20Regular />) : null}
          </div>
          <span className={styles.icon}><CategoryIcon node={node} /></span>
          <Text size={200} className={styles.ellipsis} title={node.name ?? ''}>{node.name}</Text>
          {/* Logs live in the panel below, not as rows — a single span here owned 132 of them. */}
          {logCount > 0 && (
            <Tooltip content={`${logCount} log(s) under this span — see Logs below`} relationship="description">
              <Badge appearance="outline" size="small">{logCount}</Badge>
            </Tooltip>
          )}
        </div>
        <Caption1 className={mergeClasses(styles.targetCell, styles.ellipsis)} title={node.target ?? ''}>
          {node.target}
        </Caption1>
        <Caption1 className={styles.resultCell}>
          {node.resultCode && (
            <Badge appearance="tint" size="small" color={node.success === false ? 'danger' : 'informative'}>
              {node.resultCode}
            </Badge>
          )}
        </Caption1>
        <Caption1 className={styles.durationCell}>{formatDuration(node.durationMs)}</Caption1>
        <div className={styles.track}>
          <div
            className={styles.bar}
            style={{ left: `${leftPct}%`, width: `${widthPct}%`, backgroundColor: categoryColor(node) }}
          />
        </div>
      </div>

      {open && (
        <>
          {node.exceptions.map((ex, i) => (
            <div
              key={`ex-${i}`}
              className={mergeClasses(styles.row, styles.exceptionRow, selected === ex && styles.rowSelected)}
              onClick={() => onSelect(ex)}
            >
              <div className={styles.nameCell} style={{ paddingLeft: `${(depth + 1) * 16}px` }}>
                <div className={styles.chevron} />
                <span className={styles.icon}>
                  <ErrorCircle20Filled style={{ color: tokens.colorPaletteRedForeground1 }} />
                </span>
                {/* Explicit label, as the App Insights transaction view does: an icon alone reads as
                    decoration, and the exception type on its own looks like another span name. */}
                <Caption1 className={styles.exceptionTag}>EXCEPTION</Caption1>
                <Text size={200} className={styles.ellipsis} title={ex.name ?? ex.detail ?? ''}>
                  {ex.name ?? ex.detail ?? 'Exception'}
                </Text>
              </div>
              <Caption1 className={mergeClasses(styles.targetCell, styles.ellipsis)} title={ex.method ?? ''}>
                {ex.method}
              </Caption1>
              <Caption1 className={styles.resultCell} />
              <Caption1 className={styles.durationCell} />
              <div className={styles.track} />
            </div>
          ))}
          {children.map((child, i) => (
            <SpanRows
              key={child.spanId ?? `child-${i}`}
              node={child}
              depth={depth + 1}
              originMs={originMs}
              totalMs={totalMs}
              selected={selected}
              onSelect={onSelect}
              query={query}
              keep={keep}
              styles={styles}
            />
          ))}
        </>
      )}
    </>
  );
}

/**
 * Traces, out of the tree.
 *
 * One PPE conversation carried 319 logs against 88 spans, with 132 on a single span. Rendering those
 * inline buried the structure the page exists to show, so they live here — the same split App
 * Insights makes with its "Traces & events" panel.
 */
function LogsPanel({
  logs,
  query,
  selected,
  onSelect,
  styles,
}: {
  logs: LogEntry[];
  query: string;
  selected: TelemetryNode | null;
  onSelect: (n: TelemetryNode) => void;
  styles: ReturnType<typeof useStyles>;
}) {
  const [problemsOnly, setProblemsOnly] = useState(false);

  // Warning (2) and above, not Error (3) and above. The PPE conversation that motivated this page
  // failed with four exceptions, yet its 319 logs top out at severity 2 — the useful ones being
  // "SxG MCP tool returned an error result". An errors-only threshold would have shown nothing
  // on exactly the conversation someone opened the page to debug.
  const shown = useMemo(
    () => logs.filter(({ log, spanName, isException }) => {
      // An exception is always a problem regardless of the severityLevel it carries — these land at
      // severity 2 here, and an operator filtering for trouble must never lose them.
      if (problemsOnly && !isException && (log.severityLevel ?? 0) < 2) return false;
      if (!query) return true;
      return matches(log, query) || (spanName ?? '').toLowerCase().includes(query);
    }),
    [logs, query, problemsOnly],
  );

  const problemCount = useMemo(
    () => logs.filter(({ log, isException }) => isException || (log.severityLevel ?? 0) >= 2).length,
    [logs],
  );

  if (logs.length === 0) {
    return (
      <div className={styles.tabPanel}>
        <Caption1 className={styles.empty}>No logs were recorded for this conversation.</Caption1>
      </div>
    );
  }

  return (
    // Toolbar at natural height, list fills the rest, so the list scrolls rather than the panel.
    <div className={styles.logsHost}>
      <div className={styles.logsToolbar}>
        <Caption1 className={mergeClasses(styles.empty, styles.grow)}>
          {shown.length === logs.length
            ? `${logs.length} entries`
            : `${shown.length} of ${logs.length} entries`}
        </Caption1>
        <Switch
          checked={problemsOnly}
          label={problemCount > 0 ? `Warnings & errors (${problemCount})` : 'Warnings & errors'}
          disabled={problemCount === 0}
          onChange={(_, d) => setProblemsOnly(d.checked)}
        />
      </div>
      <div className={styles.logsBody}>
        {shown.length === 0 ? (
          <div className={styles.logRow}><Caption1 className={styles.empty}>No matching logs.</Caption1></div>
        ) : (
          shown.map(({ log, spanName, isException }, i) => (
            <div
              key={`log-${i}`}
              className={mergeClasses(
                styles.logRow,
                isException && styles.exceptionRow,
                selected === log && styles.rowSelected,
              )}
              onClick={() => onSelect(log)}
            >
              <Caption1 className={styles.logTimeCell}>
                {log.timestamp ? new Date(log.timestamp).toISOString().substring(11, 23) : ''}
              </Caption1>
              <span className={styles.logSeverityCell}>
                <Badge
                  appearance={isException ? 'filled' : 'tint'}
                  size="small"
                  color={
                    isException || (log.severityLevel ?? 0) >= 3
                      ? 'danger'
                      : (log.severityLevel ?? 0) === 2
                        ? 'warning'
                        : 'informative'
                  }
                >
                  {isException ? 'Exception' : severityLabel(log.severityLevel)}
                </Badge>
              </span>
              <Caption1 className={mergeClasses(styles.logSpanCell, styles.ellipsis)} title={spanName ?? ''}>
                {spanName}
              </Caption1>
              <Caption1 className={styles.ellipsis} title={log.detail ?? log.name ?? ''}>
                {/* For an exception the type alone says little; lead with the message. */}
                {isException ? (log.detail ?? log.name) : log.name}
              </Caption1>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function DetailPane({ node, styles }: { node: TelemetryNode | null; styles: ReturnType<typeof useStyles> }) {
  if (!node) {
    return <Text className={styles.empty}>Select a span to see its details.</Text>;
  }

  const props = Object.entries(node.properties ?? {});

  return (
    <div>
      <Text weight="semibold">{node.name}</Text>
      <Divider style={{ marginTop: tokens.spacingVerticalS, marginBottom: tokens.spacingVerticalS }} />
      <DetailField label="Category" value={node.category} styles={styles} />
      <DetailField label="Type" value={node.dependencyType} styles={styles} />
      <DetailField label="Target" value={node.target} styles={styles} />
      <DetailField label="Duration" value={formatDuration(node.durationMs)} styles={styles} />
      <DetailField label="Result" value={node.resultCode} styles={styles} />
      <DetailField
        label="Success"
        value={node.success === null || node.success === undefined ? null : String(node.success)}
        styles={styles}
      />
      <DetailField label="Severity" value={node.severityLevel === null || node.severityLevel === undefined ? null : severityLabel(node.severityLevel)} styles={styles} />
      <DetailField label="Failed method" value={node.method} styles={styles} />
      <DetailField label="Assembly" value={node.assembly} styles={styles} />
      <DetailField label="Root cause type" value={node.innermostType} styles={styles} />
      <DetailField label="Root cause method" value={node.innermostMethod} styles={styles} />
      <DetailField label="Root cause message" value={node.innermostMessage} styles={styles} />
      <DetailField label="Timestamp" value={node.timestamp} styles={styles} />
      <DetailField label="Span ID" value={node.spanId} styles={styles} />

      {node.detail && (
        <div className={styles.detailRow}>
          <div className={styles.searchRow}>
            <Caption1 className={mergeClasses(styles.empty, styles.grow)}>Detail</Caption1>
            <CopyButton text={node.detail} />
          </div>
          {/* Stack traces and SQL/URLs are the reason someone opened this page — never truncate them. */}
          <div className={styles.pre}>{node.detail}</div>
        </div>
      )}

      {node.callStack && (
        <div className={styles.detailRow}>
          <div className={styles.searchRow}>
            <Caption1 className={mergeClasses(styles.empty, styles.grow)}>Call stack</Caption1>
            <CopyButton text={node.callStack} />
          </div>
          <div className={mergeClasses(styles.pre, styles.stack)}>{node.callStack}</div>
        </div>
      )}

      {props.length > 0 && (
        <div className={styles.detailRow}>
          <Caption1 className={styles.empty}>Custom dimensions</Caption1>
          <div style={{ marginTop: tokens.spacingVerticalXS }}>
            {props.map(([k, v]) => (
              <div key={k} className={styles.propRow}>
                <Caption1 className={styles.propKey}>{k}</Caption1>
                <Caption1 style={{ wordBreak: 'break-word' }}>{v}</Caption1>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <Button
      size="small"
      appearance="subtle"
      icon={<Copy20Regular />}
      onClick={() => {
        void navigator.clipboard.writeText(text).then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

function DetailField({
  label,
  value,
  styles,
}: {
  label: string;
  value?: string | null;
  styles: ReturnType<typeof useStyles>;
}) {
  if (!value) return null;
  return (
    <div className={styles.detailRow}>
      <Caption1 className={styles.empty}>{label}</Caption1>
      <Text size={200}>{value}</Text>
    </div>
  );
}
